import React, { useState } from 'react';
import { MessageCircle, Briefcase, CheckCircle, TrendingUp, FileText, HeadphonesIcon } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { CONTACT_INFO, WHATSAPP_MESSAGES, BUSINESS_PACKAGES } from '../data/mockData';
import { siteContent } from '../data/siteContent';

const Business = () => {
  const content = siteContent.business;
  const whatsappLink = `https://wa.me/${CONTACT_INFO.whatsapp}?text=${encodeURIComponent(WHATSAPP_MESSAGES.business)}`;

  // Map step icons
  const stepIcons = {
    1: FileText,
    2: HeadphonesIcon,
    3: TrendingUp,
    4: CheckCircle
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    alert('Business account application submitted! We\'ll be in touch within 24 hours.');
    window.open(whatsappLink, '_blank');
  };

  return (
    <div className="min-h-screen">
      {/* Hero */}
      <section className="bg-gradient-to-br from-[#001A47] via-[#002B6B] to-[#001A47] text-white py-16 md:py-24">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <Briefcase className="h-16 w-16 text-[#039fff] mx-auto mb-6" />
            <h1 className="text-4xl md:text-5xl font-bold mb-6">{content.hero.h1}</h1>
            <p className="text-xl text-gray-300 mb-8">
              {content.hero.subHeadline}
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a href={whatsappLink} target="_blank" rel="noopener noreferrer">
                <Button size="lg" className="bg-[#fb6402] hover:bg-[#e55a02] text-white px-8">
                  <MessageCircle className="mr-2 h-5 w-5" />
                  {content.cta.whatsapp}
                </Button>
              </a>
              <Button 
                size="lg" 
                variant="outline" 
                className="border-white text-white hover:bg-white hover:text-[#001A47]"
                onClick={() => document.getElementById('apply')?.scrollIntoView({ behavior: 'smooth' })}
              >
                {content.cta.apply}
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Who It's For */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <h2 className="text-3xl font-bold text-[#001A47] text-center mb-12">{content.sections.whoItsForTitle}</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-6xl mx-auto">
            {content.whoItsFor.map((item, index) => (
              <Card key={index} className="hover:shadow-lg transition-shadow">
                <CardContent className="p-6">
                  <TrendingUp className="h-8 w-8 text-[#039fff] mb-3" />
                  <h3 className="font-bold text-[#001A47] mb-2">{item.title}</h3>
                  <p className="text-sm text-gray-600">{item.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Packages */}
      <section className="py-16">
        <div className="container mx-auto px-4">
          <h2 className="text-3xl font-bold text-[#001A47] text-center mb-12">{content.sections.packagesTitle}</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-6xl mx-auto">
            {BUSINESS_PACKAGES.map((pkg, index) => (
              <Card key={index} className={`hover:shadow-xl transition-all ${index === 1 ? 'border-2 border-[#039fff] relative' : ''}`}>
                {index === 1 && (
                  <div className="absolute -top-4 left-1/2 transform -translate-x-1/2 bg-[#fb6402] text-white px-4 py-1 rounded-full text-sm font-semibold">
                    Most Popular
                  </div>
                )}
                <CardContent className="p-8">
                  <h3 className="text-2xl font-bold text-[#001A47] mb-2">{pkg.name}</h3>
                  <p className="text-[#039fff] font-semibold mb-6">{pkg.volume}</p>
                  <ul className="space-y-3">
                    {pkg.features.map((feature, idx) => (
                      <li key={idx} className="flex items-start text-sm">
                        <CheckCircle className="h-4 w-4 text-[#fb6402] mr-2 flex-shrink-0 mt-0.5" />
                        <span>{feature}</span>
                      </li>
                    ))}
                  </ul>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Benefits */}
      <section className="py-16 bg-[#039fff]/5">
        <div className="container mx-auto px-4">
          <h2 className="text-3xl font-bold text-[#001A47] text-center mb-12">{content.sections.benefitsTitle}</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 max-w-6xl mx-auto">
            {content.benefits.map((benefit, index) => (
              <div key={index} className="flex items-start space-x-3 bg-white p-4 rounded-lg shadow-sm">
                <CheckCircle className="h-5 w-5 text-[#039fff] flex-shrink-0 mt-0.5" />
                <p className="text-sm text-gray-700">{benefit}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="py-16">
        <div className="container mx-auto px-4">
          <h2 className="text-3xl font-bold text-[#001A47] text-center mb-12">{content.sections.howItWorksTitle}</h2>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6 max-w-5xl mx-auto">
            {content.howItWorks.map((item) => {
              const Icon = stepIcons[item.step];
              return (
                <div key={item.step} className="text-center">
                  <div className="bg-[#fb6402] text-white rounded-full w-12 h-12 flex items-center justify-center font-bold text-xl mx-auto mb-4">
                    {item.step}
                  </div>
                  <Icon className="h-8 w-8 text-[#039fff] mx-auto mb-3" />
                  <h3 className="font-bold text-[#001A47] mb-2">{item.title}</h3>
                  <p className="text-sm text-gray-600">{item.text}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Application Form */}
      <section id="apply" className="py-16 md:py-24 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="max-w-2xl mx-auto">
            <div className="text-center mb-8">
              <h2 className="text-3xl font-bold text-[#001A47] mb-4">{content.sections.applyTitle}</h2>
              <p className="text-gray-600">{content.sections.applySubtitle}</p>
            </div>

            <Card>
              <CardContent className="p-6 md:p-8">
                <form onSubmit={handleSubmit} className="space-y-4">
                  <div>
                    <Label htmlFor="business-name">Business Name *</Label>
                    <Input id="business-name" placeholder="Your company name" required />
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="contact-person">Contact Person *</Label>
                      <Input id="contact-person" placeholder="Full name" required />
                    </div>
                    <div>
                      <Label htmlFor="title">Job Title</Label>
                      <Input id="title" placeholder="e.g., Operations Manager" />
                    </div>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="email">Email Address *</Label>
                      <Input id="email" type="email" placeholder="business@example.com" required />
                    </div>
                    <div>
                      <Label htmlFor="mobile">Mobile Number *</Label>
                      <Input id="mobile" type="tel" placeholder="+27" required />
                    </div>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="volume">Estimated Monthly Volume *</Label>
                      <select id="volume" className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm" required>
                        <option value="">Select...</option>
                        <option value="1-10">1-10 deliveries/month</option>
                        <option value="11-30">11-30 deliveries/month</option>
                        <option value="31+">31+ deliveries/month</option>
                      </select>
                    </div>
                    <div>
                      <Label htmlFor="area">Primary Service Area *</Label>
                      <Input id="area" placeholder="e.g., Stellenbosch & Somerset West" required />
                    </div>
                  </div>
                  <div>
                    <Label htmlFor="business-type">Type of Business *</Label>
                    <Input id="business-type" placeholder="e.g., E-commerce, Restaurant, Property" required />
                  </div>
                  <div>
                    <Label htmlFor="notes">Additional Information</Label>
                    <textarea 
                      id="notes" 
                      placeholder="Tell us about your delivery needs..." 
                      rows={3}
                      className="flex w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                    />
                  </div>
                  <div className="flex flex-col sm:flex-row gap-3">
                    <Button type="submit" className="flex-1 bg-[#fb6402] hover:bg-[#e55a02]">
                      {content.cta.submit}
                    </Button>
                    <a href={whatsappLink} target="_blank" rel="noopener noreferrer" className="flex-1">
                      <Button type="button" variant="outline" className="w-full">
                        <MessageCircle className="mr-2 h-4 w-4" />
                        {content.cta.whatsappAlt}
                      </Button>
                    </a>
                  </div>
                </form>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Business;
